// src/v2/pulse/PortfolioCommandTile.jsx
//
// B3-PORTFOLIO-COMMAND-TILE: "Portfolio Command" tile for the Pulse leadership
// home. Displays the server-provided composite and pillar status.
//
// Score view: SVG ring, band word, live pillar meters and upcoming placeholders.
// Trend view: BarChart of weekly snapshots, sorted ascending by periodEnd.
//   Empty state when history is sparse.

import { Fragment, useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import { Check, Info } from "lucide-react";
import { T } from "../../app/tokens.js";
import { Tooltip } from "../../components/Tooltip.jsx";
import { useChartHoverIntent } from "../../components/useChartHoverIntent.js";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import {
  getCommandIndex,
  getCommandIndexHistory,
  getPortfolioCommandImages,
} from "../../data/commandIndexClient.js";
import {
  subscribeCommandIndexStale,
  unsubscribeCommandIndexStale,
  subscribeImageryInvalidation,
  unsubscribeImageryInvalidation,
} from "../../services/cacheBus.js";
import { CarouselArrowButton } from "./CarouselArrowButton.jsx";
import {
  commandPillarCountLabel,
  getLiveCommandPillars,
  getUpcomingCommandPillars,
} from "./commandPillars.js";

// ─── Constants ───────────────────────────────────────────────────────────────

const RING_R = 42;
const RING_CX = 56;
const RING_CY = 56;
const RING_STROKE = 8;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

const BAND_LABEL = {
  strong: "Strong",
  watch:  "Watch",
  risk:   "At Risk",
};

// Map band → CSS color token (v2 design system)
const BAND_COLOR = {
  strong: "var(--v2-green)",
  watch:  "var(--v2-amber)",
  risk:   "var(--v2-red)",
};

const BAND_BG = {
  strong: "var(--v2-green-tint)",
  watch:  "var(--v2-amber-tint)",
  risk:   "var(--v2-red-tint)",
};

const RETIRED_LEASE_AI_ACTION = "Run AI abstraction on leases";
const isRetiredLeaseAiCheck = (check) => check?.id === "lease_ai_abstracted";

// Strong threshold used for the tick mark on the data_trust meter
const STRONG_THRESHOLD = 80;

// Tooltip copy for the "i" icon on the card title
function infoTooltipCopy({ planEnabled, reviewsEnabled }) {
  return [
    "Portfolio Command measures how well the portfolio is managed, not how large it is.",
    "Data Trust reflects whether core portfolio data is complete and current.",
    planEnabled && "Plan Coverage shows whether locations have clear, documented plans.",
    "Review Cadence tracks whether weekly review meetings happen on time.",
    "Where to gain the most ranks the highest-leverage fixes.",
  ].filter(Boolean).join(" ");
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns true when the OS/browser has prefers-reduced-motion: reduce set.
 * Returns false in SSR / test environments where window is unavailable.
 */
function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Derive legend counts from the data_trust pillar's breakdown checks.
 * Returns { passing, needs, total } or null when no applicable checks exist.
 *
 * @param {Array<{ id: string, passing: number, applicable: number, rate: number|null }>} checks
 */
export function deriveLegendCounts(checksOrBreakdown) {
  const checks = Array.isArray(checksOrBreakdown)
    ? checksOrBreakdown
    : Array.isArray(checksOrBreakdown?.pillars)
      ? checksOrBreakdown.pillars.flatMap((pillar) => pillar.excluded ? [] : (pillar.checks ?? []))
      : Array.isArray(checksOrBreakdown?.checks)
        ? checksOrBreakdown.checks
        : null;
  if (!Array.isArray(checks) || checks.length === 0) return null;
  let totalPassing = 0;
  let totalApplicable = 0;
  for (const c of checks) {
    if (isRetiredLeaseAiCheck(c) || c.exclusionReason || c.scored === false) continue;
    totalPassing    += c.passing    ?? 0;
    totalApplicable += c.applicable ?? 0;
  }
  if (totalApplicable === 0) return null;
  return {
    passing: totalPassing,
    needs:   totalApplicable - totalPassing,
    total:   totalApplicable,
  };
}

/**
 * Sort history records ascending by periodEnd for the chart.
 * Accepts the raw camelCase array from the API.
 */
function sortHistory(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const da = a.periodEnd ?? "";
    const db = b.periodEnd ?? "";
    return da < db ? -1 : da > db ? 1 : 0;
  });
}

/**
 * Format a periodEnd date string (YYYY-MM-DD) to a short label like "Jul 20".
 */
function formatPeriodLabel(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00Z");
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * ScoreRing — SVG ring showing composite score percentage.
 * Starts arc from 12 o'clock (top center).
 * Animates from 0 → final value on mount via double-rAF + CSS transition.
 */
function ScoreRing({ composite, band }) {
  const color = (band && BAND_COLOR[band]) ?? "var(--v2-line)";
  const score = composite ?? 0;
  const arc   = (score / 100) * RING_CIRCUMFERENCE;
  const size  = RING_CX * 2;
  const reduced = prefersReducedMotion();

  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (reduced) { setReady(true); return; }
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setReady(true));
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, []);

  const displayArc   = ready ? arc : 0;
  const arcTransition = (!reduced && ready) ? "stroke-dasharray 0.6s cubic-bezier(0.4,0,0.2,1)" : undefined;

  return (
    <svg
      data-testid="cmd-score-ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`Score: ${composite ?? "—"}`}
    >
      {/* Track */}
      <circle
        cx={RING_CX}
        cy={RING_CY}
        r={RING_R}
        fill="none"
        stroke="var(--v2-line)"
        strokeWidth={RING_STROKE}
      />
      {/* Arc (counter-clockwise from top) */}
      {composite !== null && composite !== undefined && (
        <circle
          cx={RING_CX}
          cy={RING_CY}
          r={RING_R}
          fill="none"
          stroke={color}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={`${displayArc} ${RING_CIRCUMFERENCE - displayArc}`}
          strokeDashoffset={RING_CIRCUMFERENCE / 4}
          transform={`rotate(-90 ${RING_CX} ${RING_CY})`}
          style={arcTransition ? { transition: arcTransition } : undefined}
        />
      )}
      {/* Center text */}
      <text
        x={RING_CX}
        y={RING_CY - 4}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={composite !== null ? color : "var(--v2-light)"}
        fontSize={T.fsXl}
        fontWeight={T.fwDisplay}
      >
        {composite ?? "—"}
      </text>
      <text
        x={RING_CX}
        y={RING_CY + 14}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="var(--v2-mid)"
        fontSize={T.fsXs}
        fontWeight={T.fwSemibold}
      >
        / 100
      </text>
    </svg>
  );
}

/**
 * PillarMeter — one horizontal meter row.
 *
 * Shows real score (or em-dash when null), filled track, tick at STRONG_THRESHOLD%,
 * and an optional one-line sub-text.
 * Animates fill bar from 0% → final width on mount via double-rAF + CSS transition.
 *
 * @param {string}       label     - Display name (e.g. "Data Trust")
 * @param {number|null}  score     - 0–100 or null
 * @param {string|null}  subText   - One-line description shown below the bar
 * @param {string|null}  bandColor - CSS color for score number and fill bar; falls back to accent
 */
function PillarMeter({ label, score, subText, bandColor }) {
  const fillPct = score !== null ? Math.min(100, Math.max(0, score)) : 0;
  const color   = bandColor ?? "var(--v2-accent)";
  const trackH  = 6;
  const reduced = prefersReducedMotion();

  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (reduced) { setReady(true); return; }
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setReady(true));
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, []);

  const displayWidth = ready ? `${fillPct}%` : "0%";

  return (
    <div data-testid={`cmd-pillar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        marginBottom:   4,
      }}>
        {/* Name */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: "var(--v2-dark)" }}>
            {label}
          </span>
        </span>
        <span
          data-testid={`cmd-pillar-value-${label.toLowerCase().replace(/\s+/g, "-")}`}
          style={{
            fontSize:   T.fsBase,
            fontWeight: T.fwDisplay,
            color:      score !== null ? color : "var(--v2-light)",
          }}
        >
          {score !== null ? `${score}` : "—"}
        </span>
      </div>

      {/* Track + tick wrapper */}
      <div style={{ position: "relative" }}>
        {/* Track (overflow hidden — only clips fill bar) */}
        <div style={{ position: "relative", height: trackH, borderRadius: 999, overflow: "hidden" }}>
          {/* Background track */}
          <div style={{ position: "absolute", inset: 0, background: "var(--v2-hair)", borderRadius: 999 }} />

          {/* Fill bar */}
          <div style={{
            position:     "absolute",
            left:         0,
            top:          0,
            bottom:       0,
            width:        displayWidth,
            background:   color,
            borderRadius: 999,
            transition:   reduced ? undefined : "width 0.55s ease-out",
          }} />
        </div>

        {/* Tick mark at STRONG_THRESHOLD% */}
        <div style={{
          position:     "absolute",
          left:         `${STRONG_THRESHOLD}%`,
          top:          0,
          height:       trackH,
          width:        2,
          background:   "var(--v2-dark)",
          opacity:      0.35,
          borderRadius: 1,
        }} />
      </div>

      {/* One-line sub-text */}
      {subText && (
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 3 }}>
          {subText}
        </div>
      )}
    </div>
  );
}

function UpcomingPillar({ pillar }) {
  return (
    <div data-testid={`cmd-upcoming-${pillar.key}`} style={{ padding: "6px 8px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: "var(--v2-light)" }}>{pillar.label}</span>
        <span style={{
          fontSize: T.fsXs, color: "var(--v2-mid)", background: T.lightGray,
          border: `1px solid ${T.border}`, borderRadius: 999, padding: "2px 7px",
          whiteSpace: "nowrap",
        }}>{pillar.status === "excluded" ? "Excluded" : "Coming soon"}</span>
      </div>
      <div aria-hidden="true" style={{
        height: 6, borderRadius: 999, background: T.lightGray, marginTop: 4,
      }} />
    </div>
  );
}

/**
 * LegendChip — a small colored swatch + label chip.
 */
function LegendChip({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{
        display:      "inline-block",
        width:        8,
        height:       8,
        borderRadius: "50%",
        background:   color,
        flexShrink:   0,
      }} />
      <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>{label}</span>
    </span>
  );
}

/**
 * extractSubject — returns the part after " for " in an action string,
 * or the full string when the separator is absent.
 * e.g. "Set a plan for New York" → "New York"
 */
function extractSubject(action) {
  if (!action) return action ?? "";
  const idx = action.indexOf(" for ");
  return idx >= 0 ? action.slice(idx + 5) : action;
}

/**
 * extractVerb — returns the part before " for " in an action string,
 * or "" when the separator is absent.
 * e.g. "Set a plan for New York" → "Set a plan"
 */
function extractVerb(action) {
  if (!action) return "";
  const idx = action.indexOf(" for ");
  return idx >= 0 ? action.slice(0, idx) : "";
}

/**
 * OpportunityTile — fixed-width portrait card for "Where to gain the most".
 *
 * @param {{ measure, measureBand, action, meta, liftPoints, effort, navTarget }} opp
 * @param {number}   index   - Tile index (0-based) for testid
 * @param {Function} onClick - Called when the tile is clicked
 */
const COMMAND_CARD_WIDTH = 196;
const COMMAND_CARD_HEIGHT = 276;
const COMMAND_CARDS_PER_PAGE = 2;

function opportunityMarketId(opp) {
  if (!opp?.navTarget) return null;
  try {
    return new URLSearchParams(String(opp.navTarget).replace(/^[^?]*\??/, "")).get("marketId");
  } catch {
    return null;
  }
}

function opportunityEnabled(opp, planEnabled, reviewsEnabled) {
  const pillar = opportunityPillarKey(opp);
  if (pillar === "plan_coverage" && !planEnabled) return false;
  // Weekly Review Cadence is independent of the legacy monthly Reviews navigation flag.
  const target = String(opp?.navTarget ?? "");
  const query = target.includes("?") ? target.slice(target.indexOf("?") + 1) : target;
  const view = new URLSearchParams(query).get("view");
  if (["plan", "plan-coverage", "whatif"].includes(view)) return planEnabled;
  if (view === "review-cadence") return true;
  if (["review", "reviews"].includes(view)) return reviewsEnabled;
  return true;
}

function opportunityPhoto(opp, markets, locations) {
  const marketId = opportunityMarketId(opp);
  if (!marketId) return null;
  const market = Array.isArray(markets) ? markets.find((item) => item?.id === marketId) : null;
  if (market?.photoUrl || market?.imageUrl) return market.photoUrl ?? market.imageUrl;
  const location = Array.isArray(locations)
    ? locations.find((item) => item?.marketId === marketId && item?.imageUrl)
    : null;
  return location?.imageUrl ?? null;
}

const PILLAR_KEYS = new Set(["data_trust", "plan_coverage", "review_cadence"]);

/**
 * Canonicalize the server-provided measure label to the Image Library key.
 * The command-index currently sends display labels, but accepting canonical
 * keys keeps this resolver coupled to the contract rather than typography.
 */
export function opportunityPillarKey(opp) {
  const normalized = String(opp?.measure ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return PILLAR_KEYS.has(normalized) ? normalized : null;
}

/**
 * Resolve a gain tile visual in the documented precedence order:
 * location-backed image, then Portfolio Command pillar image, then the
 * existing neutral gradient.
 */
export function resolveOpportunityVisual(opp, markets, locations, pillarImages = {}) {
  const locationUrl = opportunityPhoto(opp, markets, locations);
  if (locationUrl) return { url: locationUrl, source: "location" };
  const pillarKey = opportunityPillarKey(opp);
  const pillarUrl = pillarKey ? pillarImages?.[pillarKey] : null;
  if (pillarUrl) return { url: pillarUrl, source: "pillar" };
  return { url: null, source: "gradient" };
}

function opportunityGradient(opp) {
  const action = String(opp?.action ?? "").toLowerCase();
  if (action.includes("headcount") || action.includes("capacity")) return T.commandGradHeadcount;
  if (action.includes("lease")) return T.commandGradLease;
  if (action.includes("abstract")) return T.commandGradAbstraction;
  return T.commandGradDefault;
}

function OpportunityTile({ opp, index, onClick, markets, locations, pillarImages, testIdPrefix = "cmd-opp" }) {
  const [imageBroken, setImageBroken] = useState(false);
  const pillarBg    = (opp.measureBand && BAND_BG[opp.measureBand]) ?? "var(--v2-amber-tint)";
  const pillarColor = (opp.measureBand && BAND_COLOR[opp.measureBand]) ?? "var(--v2-amber)";
  const { url: photoUrl, source: visualSource } = resolveOpportunityVisual(opp, markets, locations, pillarImages);
  const isPhoto   = Boolean(photoUrl) && !imageBroken;
  const title     = opp.action ?? extractSubject(opp.action);
  const rationale = opp.meta
    ? opp.verbatimMeta ? opp.meta : `${extractVerb(opp.action) ? `${extractVerb(opp.action)} · ` : ""}${opp.meta}`
    : "This is the highest-leverage next step.";
  const pillar    = opp.measure ?? "Portfolio";
  const effort    = Number(opp.effort);

  useEffect(() => { setImageBroken(false); }, [photoUrl]);

  function handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  }
  return (
    <article
      data-testid={`${testIdPrefix}-tile-${index}`}
      data-visual-variant={isPhoto ? "photo" : "gradient"}
      data-visual-source={isPhoto ? visualSource : "gradient"}
      className="cmd-opp-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={{
        position: "relative",
        display: "flex",
        flex: `0 0 ${COMMAND_CARD_WIDTH}px`,
        width: COMMAND_CARD_WIDTH,
        height: COMMAND_CARD_HEIGHT,
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid var(--v2-line)",
        borderRadius: 13,
        background: "var(--v2-card)",
        boxShadow: "0 2px 8px rgba(16,36,58,.08)",
        cursor: "pointer",
        outline: "none",
      }}
    >
      <div
        data-testid={`${testIdPrefix}-visual-${index}`}
        className="cmd-opp-visual"
        style={{
          position: "relative",
          height: 96,
          flexShrink: 0,
          overflow: "hidden",
          background: isPhoto ? "var(--v2-tint)" : opportunityGradient(opp),
        }}
      >
        {isPhoto && (
          <img
            data-testid={`${testIdPrefix}-image-${index}`}
            src={photoUrl}
            alt=""
            loading="lazy"
            onError={() => setImageBroken(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: isPhoto
            ? "linear-gradient(180deg, rgba(10,18,36,.08), rgba(10,18,36,.58))"
            : "linear-gradient(180deg, rgba(255,255,255,.06), rgba(0,0,0,.12))",
        }} />
        {opp.liftPoints !== null && opp.liftPoints !== undefined && (
          <span
            data-testid={`${testIdPrefix}-lift-${index}`}
            style={{
              position: "absolute", top: 10, right: 10, zIndex: 1,
              padding: "4px 7px", borderRadius: 6,
              background: "var(--v2-green-tint)", color: "var(--v2-green)",
              fontSize: T.fsXs, fontWeight: T.fwBold, whiteSpace: "nowrap",
            }}
          >
            +{opp.liftPoints} pts
            {effort > 1 && <span data-testid={`${testIdPrefix}-effort-${index}`}> · {effort} items</span>}
          </span>
        )}
        <span
          data-testid={`${testIdPrefix}-rank-${index}`}
          style={{
          position: "absolute", top: 10, left: 10, zIndex: 1,
          display: "grid", placeItems: "center", width: 22, height: 22,
          borderRadius: "50%", background: "rgba(255,255,255,.92)",
          color: "var(--v2-dark)", fontSize: T.fsXs, fontWeight: T.fwBold,
        }}>
          {index + 1}
        </span>
      </div>
      <div style={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column", padding: "11px 12px 12px" }}>
        <span
          data-testid={`${testIdPrefix}-pillar-${index}`}
          style={{
            alignSelf: "flex-start", maxWidth: "100%", overflow: "hidden",
            padding: "3px 7px", borderRadius: 5,
            background: pillarBg, color: pillarColor,
            fontSize: T.fsXs, fontWeight: T.fwBold,
            letterSpacing: ".04em", textTransform: "uppercase",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {pillar}
        </span>
        <div
          data-testid={`${testIdPrefix}-action-${index}`}
          style={{
            marginTop: 9, color: "var(--v2-dark)", fontSize: T.fsBase,
            fontWeight: T.fwBold, lineHeight: 1.25,
            display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2,
            overflow: "hidden", textOverflow: "ellipsis",
          }}
          title={title}
        >
          {title}
        </div>
        <div
          data-testid={`${testIdPrefix}-rationale-${index}`}
          style={{
            marginTop: 5, color: "var(--v2-mid)", fontSize: T.fsXs,
            lineHeight: 1.4, display: "-webkit-box", WebkitBoxOrient: "vertical",
            WebkitLineClamp: 4, overflow: "hidden", textOverflow: "ellipsis",
          }}
          title={rationale}
        >
          {rationale}
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          data-testid={`${testIdPrefix}-go-${index}`}
          onClick={(event) => { event.stopPropagation(); onClick?.(); }}
          style={{
            alignSelf: "flex-start", border: 0, padding: 0, background: "transparent",
            color: "var(--v2-accent)", cursor: "pointer",
            fontSize: T.fsSm, fontWeight: T.fwBold,
          }}
        >
          Go to action <span aria-hidden="true">→</span>
        </button>
      </div>
    </article>
  );
}

/**
 * InfoTooltip — small "i" icon with hover tooltip showing rollout copy.
 * Uses a CSS-driven hover state (no third-party library).
 */
function InfoTooltip({ text }) {
  const { open: visible, triggerProps } = useHoverIntent();
  return (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      {...triggerProps}
      tabIndex={0}
    >
      <Info
        size={13}
        data-testid="cmd-info-icon"
        style={{ color: "var(--v2-mid)", cursor: "help" }}
      />
      {visible && (
        <Tooltip
          data-testid="cmd-info-tooltip"
          style={{
            top:       "calc(100% + 6px)",
            left:      "50%",
            transform: "translateX(-50%)",
            whiteSpace: "normal",
            width:     260,
            maxWidth:  260,
            zIndex:    20,
          }}
        >
          {text}
        </Tooltip>
      )}
    </span>
  );
}

/**
 * SegmentedControl — Score / Trend toggle.
 *
 * Kept with the Trend implementation so the alternate view can be
 * re-enabled without rebuilding it.
 */
function SegmentedControl({ value, onChange }) {
  const opts = [
    { value: "score", label: "Score" },
    { value: "trend", label: "Trend" },
  ];
  return (
    <div
      data-testid="cmd-seg-control"
      style={{
        display:      "inline-flex",
        background:   "var(--v2-hair)",
        borderRadius: 10,
        padding:      3,
      }}
    >
      {opts.map((opt) => (
        <button
          key={opt.value}
          data-testid={`cmd-seg-${opt.value}`}
          onClick={() => onChange(opt.value)}
          style={{
            border:       0,
            padding:      "5px 12px",
            borderRadius: 8,
            fontSize:     T.fsBase,
            fontWeight:   T.fwBold,
            cursor:       "pointer",
            background:   value === opt.value ? "var(--v2-card)" : "transparent",
            color:        value === opt.value ? "var(--v2-dark)" : "var(--v2-mid)",
            boxShadow:    value === opt.value ? "0 1px 4px rgba(16,36,58,.1)" : "none",
            transition:   "all .15s var(--v2-ease)",
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * TrendBar — Recharts bar tooltip formatter.
 */
export function TrendTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const band  = entry?.payload?.band;
  const color = (band && BAND_COLOR[band]) ?? "var(--v2-accent)";
  return (
    <div style={{
      background:   "var(--v2-tooltip-bg)",
      color:        "var(--v2-tooltip-fg)",
      fontSize:     T.tooltip.fontSize,
      fontWeight:   T.tooltip.fontWeight,
      padding:      "6px 10px",
      borderRadius: 6,
      lineHeight:   T.tooltip.lineHeight,
    }}>
      <div style={{ fontWeight: T.fwBold }}>{label}</div>
       <div style={{ color, fontWeight: T.tooltip.fontWeight }}>{entry.value} — {BAND_LABEL[band] ?? band}</div>
    </div>
  );
}

// ─── Score View ───────────────────────────────────────────────────────────────

function ScoreView({ indexData, onNavigate, markets, locations, pillarImages, planEnabled, reviewsEnabled }) {
  const [opportunityPage, setOpportunityPage] = useState(0);
  const [dtHover,       setDtHover]       = useState(false);
  const [cadenceHover,  setCadenceHover]  = useState(false);
  const { composite, band, pillars } = indexData ?? {};
  const opportunities = (Array.isArray(indexData?.opportunities) ? indexData.opportunities : [])
    .filter((opp) => opp.action !== RETIRED_LEASE_AI_ACTION)
    .filter((opp) => opportunityEnabled(opp, planEnabled, reviewsEnabled));
  const hasScoredGains = Number.isFinite(composite)
    && Object.hasOwn(BAND_LABEL, band)
    && Array.isArray(indexData?.opportunities);
  const bandColor = (band && BAND_COLOR[band]) ?? "var(--v2-light)";
  const bandLabel = (band && BAND_LABEL[band]) ?? "—";
  const dataTrustPillar = pillars?.find((pillar) =>
    pillar.key === "data_trust" && pillar.status === "live" && Number.isFinite(pillar.score));

  // Legend: derive from data_trust checks
  const dtBreakdown = dataTrustPillar?.breakdown ?? null;
  const legend   = dtBreakdown ? deriveLegendCounts(dtBreakdown) : null;

  // Sub-text lines derived from breakdown data
  const dtSubText = (() => {
    if (!legend) return null;
    return `${legend.passing} of ${legend.total} freshness checks clear - ${legend.needs} need attention`;
  })();

  const safeNavigate = (target) => {
    if (!opportunityEnabled({ navTarget: target }, planEnabled, reviewsEnabled)) return;
    onNavigate?.(target);
  };

  return (
    <div data-testid="cmd-score-view">
      <style>{`
        .cmd-score-layout {
          display: grid;
          grid-template-columns: 240px minmax(0, 1fr);
          gap: 24px;
          align-items: start;
        }
        .cmd-score-left { min-width: 0; }
        .cmd-score-summary {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 10px;
        }
        .cmd-meter-stack {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .cmd-meter-drilldown:focus-visible {
          outline: 2px solid var(--v2-accent);
          outline-offset: 2px;
        }
        .cmd-opportunity-panel { min-width: 0; padding-left: 2px; }
        .cmd-opp-grid {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 2px 2px 8px;
          scrollbar-width: thin;
        }
        .cmd-opp-card:focus-visible {
          box-shadow: 0 0 0 3px var(--v2-blue-tint), 0 2px 8px rgba(16,36,58,.08);
        }
        .cmd-opp-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(16,36,58,.12);
        }
        .cmd-second-order-caption {
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px solid var(--v2-line);
          color: var(--v2-mid);
          font-size: ${T.fsXs}px;
          line-height: 1.45;
        }
        @media (max-width: 560px) {
          .cmd-score-layout { grid-template-columns: 1fr; gap: 14px; }
          .cmd-opportunity-panel { padding-left: 0; }
        }
      `}</style>
      <div className="cmd-score-layout" data-testid="cmd-score-layout">

        {/* Left column: ring + band word pill + caption */}
        <div className="cmd-score-left">
          <div className="cmd-score-summary">
            <ScoreRing composite={composite} band={band} />
            <div>
              <span
                data-testid="cmd-band-word"
                style={{
                  color:      band ? bandColor : "var(--v2-light)",
                  fontSize:   T.fsLg,
                  fontWeight: T.fwBold,
                  display:    "block",
                }}
              >
                {bandLabel}
              </span>
              <div
                data-testid="cmd-score-context"
                style={{
                  color:      "var(--v2-mid)",
                  fontSize:   T.fsXs,
                  fontWeight: T.fwSemibold,
                  marginTop:  5,
                }}
              >
                Composite · {commandPillarCountLabel(indexData)}
              </div>
            </div>
          </div>

        <div className="cmd-meter-stack" data-testid="cmd-meter-stack">
          {/* Data Trust meter — drill-in to Data Trust drilldown */}
          {dataTrustPillar && <button
            type="button"
            className="cmd-meter-drilldown"
            data-testid="cmd-datatrust-meter-btn"
            onClick={() => onNavigate?.("?view=datatrust")}
            onMouseEnter={() => setDtHover(true)}
            onMouseLeave={() => setDtHover(false)}
            style={{
              all:          "unset",
              display:      "block",
              cursor:       onNavigate ? "pointer" : "default",
              borderRadius: 8,
              padding:      "6px 8px",
              background:   dtHover && onNavigate ? "var(--v2-tint)" : "transparent",
              transition:   "background .15s var(--v2-ease)",
            }}
            tabIndex={onNavigate ? 0 : -1}
            aria-label="Open Data Trust drill-down"
          >
            <PillarMeter
              label={dataTrustPillar?.label ?? "Data Trust"}
              score={dataTrustPillar?.score ?? null}
              subText={dtSubText}
              bandColor={dataTrustPillar?.band ? BAND_COLOR[dataTrustPillar.band] : undefined}
            />
            {onNavigate && (
              <span style={{ display: "block", color: "var(--v2-light)", fontSize: T.fsXs, marginTop: 2 }}>
                Click to drill in
              </span>
            )}
          </button>}

          {getLiveCommandPillars(indexData)
            .filter((pillar) => pillar.key !== "data_trust")
            .map((pillar) => {
              const row = pillars?.find((item) => item.key === pillar.key);
              const meter = <PillarMeter
                label={row?.label ?? pillar.label}
                score={row?.score ?? null}
                bandColor={row?.band ? BAND_COLOR[row.band] : undefined}
              />;
              if (pillar.key !== "review_cadence") return <Fragment key={pillar.key}>{meter}</Fragment>;
              return <button
                key={pillar.key}
                type="button"
                className="cmd-meter-drilldown"
                data-testid="cmd-cadence-meter-btn"
                onClick={() => onNavigate?.("?view=review-cadence")}
                onMouseEnter={() => setCadenceHover(true)}
                onMouseLeave={() => setCadenceHover(false)}
                style={{
                  all:        "unset",
                  display:    "block",
                  cursor:     onNavigate ? "pointer" : "default",
                  borderRadius: 8,
                  padding:    "6px 8px",
                  background: cadenceHover && onNavigate ? "var(--v2-tint)" : "transparent",
                  transition: "background .15s var(--v2-ease)",
                }}
                tabIndex={onNavigate ? 0 : -1}
                aria-label="Open Review Cadence drill-down"
              >
                {meter}
                {onNavigate && (
                  <span style={{ display: "block", color: "var(--v2-light)", fontSize: T.fsXs, marginTop: 2 }}>
                    Click to drill in
                  </span>
                )}
              </button>;
            })}
          {getUpcomingCommandPillars(indexData)
            .map((pillar) => <UpcomingPillar key={pillar.key}
              pillar={{ ...pillar, status: pillars?.find((row) => row.key === pillar.key)?.status }} />)}
        </div>
      </div>

      <div className="cmd-opportunity-panel" data-testid="cmd-opportunity-panel">
        {/* Where to gain the most */}
        {(() => {
        const opps = opportunities;
        const pageCount = Math.ceil(opps.length / COMMAND_CARDS_PER_PAGE);
        const lastPage = Math.max(0, pageCount - 1);
        const page = Math.min(opportunityPage, lastPage);
        return (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom:  6,
              }}
            >
              <div
                data-testid="cmd-opp-section-label"
                style={{
                  fontSize: T.fsXs,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: T.mid,
                  fontWeight: T.fwBold,
                }}
              >
                Where to gain the most
              </div>
              {opps.length > COMMAND_CARDS_PER_PAGE && <div style={{ display: "flex", gap: 6 }}>
                <CarouselArrowButton
                  direction="previous"
                  enabled={page > 0}
                  label="Previous opportunities"
                  testId="cmd-opp-prev"
                  onClick={() => setOpportunityPage((current) => Math.max(0, current - 1))}
                />
                <CarouselArrowButton
                  direction="next"
                  enabled={page < lastPage}
                  label="Next opportunities"
                  testId="cmd-opp-next"
                  onClick={() => setOpportunityPage((current) => Math.min(lastPage, current + 1))}
                />
              </div>}
            </div>
            {opps.length === 0 ? (
              hasScoredGains ? (
                <div
                  data-testid="cmd-opp-empty"
                  style={{
                    minHeight: COMMAND_CARD_HEIGHT,
                    border: "1px dashed var(--v2-line)",
                    borderRadius: 12,
                    background: "var(--v2-green-tint)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: "24px",
                  }}
                >
                  <span aria-hidden="true" style={{
                    width: 52, height: 52, borderRadius: "50%",
                    background: "var(--v2-card)", color: "var(--v2-green)",
                    display: "grid", placeItems: "center", marginBottom: 12,
                  }}>
                    <Check size={28} strokeWidth={2.5} />
                  </span>
                  <strong style={{ color: "var(--v2-dark)", fontSize: T.fsBase, marginBottom: 5 }}>
                    You're all caught up
                  </strong>
                  <span style={{ color: "var(--v2-mid)", fontSize: T.fsBase, lineHeight: 1.5, maxWidth: 420 }}>
                    No actions to take right now. New actions appear automatically when a check needs attention.
                  </span>
                </div>
              ) : (
                <div data-testid="cmd-opp-unavailable" style={{ color: "var(--v2-mid)", fontSize: T.fsBase, padding: "24px 0" }}>
                  Gain opportunities unavailable
                </div>
              )
            ) : <div
              data-testid="cmd-opp-grid"
              style={{
                overflowX: "hidden",
                overflowY: "hidden",
                padding: "2px 2px 8px",
              }}
            >
              <div
                data-testid="cmd-opp-track"
                style={{
                  display: "flex",
                  gap: 12,
                  transform: `translateX(-${page * COMMAND_CARDS_PER_PAGE * (COMMAND_CARD_WIDTH + 12)}px)`,
                  transition: prefersReducedMotion() ? "none" : "transform 180ms ease",
                }}
              >
                {opps.map((opp, i) => (
                  <OpportunityTile
                    key={`${opp.navTarget ?? ""}:${opp.action ?? i}`}
                    opp={opp}
                    index={i}
                    onClick={() => safeNavigate(opp.navTarget)}
                    markets={markets}
                    locations={locations}
                    pillarImages={pillarImages}
                  />
                ))}
              </div>
            </div>}
            <div className="cmd-second-order-caption" data-testid="cmd-second-order-caption">
              Second-order pane: grades how well the portfolio is being managed, so it closes the page rather than opening it.
            </div>
          </>
        );
        })()}

      </div>
    </div>
    </div>
  );
}

// ─── Trend View ───────────────────────────────────────────────────────────────

export function TrendView({ history }) {
  const intent = useChartHoverIntent(JSON.stringify(history));
  const rows = sortHistory(history);

  const isEmpty = rows.length === 0;

  const chartData = rows.map((r) => ({
    label:     formatPeriodLabel(r.periodEnd),
    composite: r.composite ?? 0,
    band:      r.band,
  }));

  return (
    <div data-testid="cmd-trend-view">
      {isEmpty ? (
        <div style={{
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          justifyContent: "center",
          minHeight:      160,
          color:          "var(--v2-light)",
        fontSize:       T.fsBase,
          textAlign:      "center",
          gap:            8,
        }}>
          <span style={{ fontSize: T.fsLg, color: "var(--v2-line)" }}>—</span>
          <span>History builds over time.</span>
          <span style={{ fontSize: T.fsXs }}>More history appears each period.</span>
        </div>
      ) : (
        <>
          <div {...intent.containerProps} style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart {...intent.chartProps} data={chartData} barCategoryGap="30%">
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: T.fsXs, fill: "var(--v2-light)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: T.fsXs, fill: "var(--v2-light)" }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                />
                <RechartsTooltip
                  {...intent.tooltipProps}
                  content={<TrendTooltipContent />}
                  cursor={{ fill: "var(--v2-tint)" }}
                />
                <Bar dataKey="composite" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={(entry.band && BAND_COLOR[entry.band]) ?? "var(--v2-accent)"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{
            fontSize:   T.fsXs,
            color:      "var(--v2-light)",
            marginTop:  10,
            textAlign:  "center",
          }}>
            Snapshot taken weekly. More history appears each period.
          </div>
        </>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function TileSkeleton() {
  return (
    <div data-testid="cmd-skeleton" style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 0" }}>
      {/* Ring placeholder */}
      <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
        <div style={{ width: 112, height: 112, borderRadius: "50%", background: "var(--v2-hair)" }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ height: 20, width: "40%", borderRadius: 999, background: "var(--v2-hair)" }} />
          <div style={{ height: 12, width: "70%", borderRadius: 4, background: "var(--v2-hair)" }} />
        </div>
      </div>
      {/* Meter placeholders */}
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ height: 11, width: "55%", borderRadius: 4, background: "var(--v2-hair)" }} />
          <div style={{ height: 6, width: "100%", borderRadius: 999, background: "var(--v2-hair)" }} />
        </div>
      ))}
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

function TileError() {
  return (
    <div
      data-testid="cmd-error"
      style={{
        padding:        "18px 0",
        textAlign:      "center",
        fontSize:       T.fsBase,
        color:          "var(--v2-light)",
      }}
    >
      Portfolio Command unavailable
    </div>
  );
}

// ─── Root export ─────────────────────────────────────────────────────────────

/**
 * PortfolioCommandTile
 *
 * Self-contained tile shown at the top of the Pulse page. Fetches both the
 * live command-index and its history in parallel on mount. Renders a Score
 * score view. The Trend implementation remains in this module for future
 * reuse but is intentionally not reachable from the Home pane.
 *
 * @param {Function} [onNavigate] - Called with a ?view= URL string when the
 *   user clicks through to a drill-down (e.g. ?view=datatrust).
 */
export function PortfolioCommandTile({
  onNavigate,
  markets = [],
  locations = [],
  planEnabled = true,
  reviewsEnabled = true,
}) {
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(false);
  const [indexData, setIndexData] = useState(null);
  const [history,   setHistory]   = useState([]);
  const [pillarImages, setPillarImages] = useState({});

  useEffect(() => {
    Promise.allSettled([
      getCommandIndex(),
      getCommandIndexHistory(),
      getPortfolioCommandImages(),
    ]).then(([idxResult, histResult, imageryResult]) => {
      if (idxResult.status === "fulfilled" && idxResult.value) {
        setIndexData(idxResult.value);
      } else if (idxResult.status === "rejected") {
        setError(true);
      }
      if (histResult.status === "fulfilled" && Array.isArray(histResult.value)) {
        setHistory(histResult.value);
      }
      if (imageryResult.status === "fulfilled" && imageryResult.value) {
        setPillarImages(imageryResult.value);
      }
      setLoading(false);
    });
  }, []);

  // Re-fetch command index when a plan save may have changed Plan Coverage.
  useEffect(() => {
    function handleStale() {
      getCommandIndex()
        .then((result) => {
          if (!result?.pillars) throw new Error("Index unavailable");
          setIndexData(result);
          setError(false);
        })
        .catch(() => setError(true));
    }
    subscribeCommandIndexStale(handleStale);
    return () => { unsubscribeCommandIndexStale(handleStale); };
  }, []);

  // Successful Image Library writes emit this event after the server response.
  // Re-read immediately; there is no TTL, polling loop, or local cache.
  useEffect(() => {
    function handleImageryInvalidation() {
      getPortfolioCommandImages()
        .then((result) => { if (result) setPillarImages(result); })
        .catch(() => {});
    }
    subscribeImageryInvalidation(handleImageryInvalidation);
    return () => { unsubscribeImageryInvalidation(handleImageryInvalidation); };
  }, []);

  return (
    <div
      data-testid="cmd-portfolio-command-tile"
      style={{
        background:     "var(--v2-card)",
        border:         "1px solid var(--v2-line)",
        borderRadius:   16,
        boxShadow:      "var(--v2-shadow-card)",
        padding:        "18px 20px",
      }}
    >
      {/* Header row: title + info tooltip */}
      <div data-testid="cmd-header-row" style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        marginBottom:   6,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            display:      "inline-block",
            width:        3,
            height:       14,
            background:   "var(--v2-accent)",
            borderRadius: 2,
          }} />
          <span data-testid="cmd-portfolio-command-title" style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
            Portfolio command
          </span>
          <InfoTooltip text={infoTooltipCopy({ planEnabled, reviewsEnabled })} />
        </div>
      </div>

      {loading ? (
        <TileSkeleton />
      ) : error || !indexData ? (
        <TileError />
      ) : (
        <ScoreView
          indexData={indexData}
          onNavigate={onNavigate}
          markets={markets}
          locations={locations}
          pillarImages={pillarImages}
          planEnabled={planEnabled}
          reviewsEnabled={reviewsEnabled}
        />
      )}
    </div>
  );
}
